import { useState, useCallback } from 'react';
import { createContainer } from 'unstated-next';
import { useDataProvider, useGetOne } from 'react-admin';
import {
  RegionDto,
  SpSubdivisionNameInterface,
  SubdivisionDto,
  SubdivisionOverrideTypeEnum,
} from '@vatos-pas/common';
import {
  batchBuilderSupervisor,
  batchSupervisorSubdivision,
} from 'services/auth';

export interface SupervisorOption {
  id: string;
  name: string;
}

export interface UserRoleRegion {
  id: string;
  firstName: string;
  lastName: string;
  roleNames: string;
  regionIds: string;
}

export interface SubDivisionFormValues {
  name: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  builderId: string;
  regionId: string;
  active: boolean;
  warrantyStatus: string;
  reqBumpoutTimesheet?: boolean;
  phaseStructureType: string;
  overrideRegionCostPerBoard?: boolean;
  overrideType?: SubdivisionOverrideTypeEnum;
  // Drywall fields
  dwHangingCostPerBoard?: number | null;
  dwFinishingCostPerBoard?: number | null;
  dwSprayingCostPerBoard?: number | null;
  // DrywallAndPaint fields
  dwpHangingCostPerBoard?: number | null;
  dwpFinishCostPerBoard?: number | null;
  dwpPrimeCostPerSqft?: number | null;
  dwpTrim1CostPerSqft?: number | null;
  dwpBump1CostPerBoard?: number | null;
  dwpInteriorPaintCostPerSqft?: number | null;
  dwpBump2CostPerBoard?: number | null;
  dwpTrim2CostPerSqft?: number | null;
  dwpFinalPaintCostPerSqft?: number | null;
  dwpBump3CostPerBoard?: number | null;
  dwpCMBluetapeCostPerSqft?: number | null;
  dwpFinalBluetapeCostPerSqft?: number | null;
  // Common cost fields
  pieceworkPercentage?: number | null;
  specialJobsPercentage?: number | null;
  garageCostFixed?: number | null;
  highCeilingPerBoard?: number | null;
  screwInspectionPerBoard?: number | null;
  interiorOptionsPercentage?: number | null;
  exteriorOptionsPercentage?: number | null;
  trimOptionPercentage?: number | null;
  geoDistanceTolerance: number | null;
  allowPayoutAmountEditing: boolean;
}

/**
 * SubDivision Container - Data Access Layer
 *
 * SWR BEHAVIOR: Uses react-admin's useGetOne which caches data in Redux store.
 * Cached data is returned immediately while fresh data is fetched in the
 * background. Loading spinner only shows on first load when no cached data
 * exists. Cache persists across navigations via Redux.
 */
function useSubDivision() {
  const dataProvider = useDataProvider();

  const [subdivisionId, setSubdivisionId] = useState<string>('');
  const {
    data: selectedSubdivision,
    loading: subdivisionLoading,
    loaded: subdivisionLoaded,
    error: subdivisionError,
  } = useGetOne<SubdivisionDto>('subdivision', subdivisionId, {
    enabled: !!subdivisionId,
  });

  const isLoading =
    !!subdivisionId && !selectedSubdivision && subdivisionLoading;
  const isRevalidating = !!subdivisionId && subdivisionLoading;

  const [formLoading, setFormLoading] = useState(false);
  const [supervisors, setSupervisors] = useState<UserRoleRegion[]>([]);
  const [builderSupervisors, setBuilderSupervisors] = useState<
    UserRoleRegion[]
  >([]);
  const [supervisorsLoading, setSupervisorsLoading] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<RegionDto | null>(null);
  const [regionLoading, setRegionLoading] = useState(false);
  const [spSubdivisions, setSpSubdivisions] = useState<
    SpSubdivisionNameInterface[]
  >([]);
  const [spSubdivisionsLoading, setSpSubdivisionsLoading] = useState(false);

  const loadSubdivisionById = useCallback((id: string): void => {
    setSubdivisionId(id);
  }, []);

  const createSubdivision = useCallback(
    async (data: Record<string, any>): Promise<SubdivisionDto> => {
      setFormLoading(true);
      try {
        const result = await dataProvider.create<SubdivisionDto>(
          'subdivision',
          {
            data,
          },
        );
        const subdivision = result.data;
        setSubdivisionId(subdivision.id);
        return subdivision;
      } finally {
        setFormLoading(false);
      }
    },
    [dataProvider],
  );

  const updateSubdivision = useCallback(
    async (
      id: string,
      data: Record<string, unknown>,
    ): Promise<SubdivisionDto> => {
      if (!selectedSubdivision) {
        throw new Error('Cannot update: subdivision data not loaded');
      }
      setFormLoading(true);
      try {
        // Need to remove these fields because they are relations
        // It causes backend to throw an error
        const {
          region: _region,
          subdivisionSupervisors: _subdivisionSupervisors,
          ...subdivision
        } = selectedSubdivision;

        const result = await dataProvider.update<SubdivisionDto>(
          'subdivision',
          {
            id,
            data: { ...subdivision, ...data },
            previousData: selectedSubdivision,
          },
        );
        return result.data;
      } finally {
        setFormLoading(false);
      }
    },
    [dataProvider, selectedSubdivision],
  );

  const loadRegion = useCallback(
    async (regionId: string): Promise<RegionDto | null> => {
      setRegionLoading(true);
      try {
        const result = await dataProvider.getOne<RegionDto>('region', {
          id: regionId,
        });
        if (result?.data) {
          setSelectedRegion(result.data);
          return result.data;
        }
        return null;
      } catch (error) {
        console.error('Failed to load region:', error);
        return null;
      } finally {
        setRegionLoading(false);
      }
    },
    [dataProvider],
  );

  const loadSupervisors = useCallback(
    async (regionId: string): Promise<void> => {
      setSupervisorsLoading(true);
      try {
        const response = await dataProvider.getList<UserRoleRegion>(
          'user-role-region-list',
          {
            filter: {
              'regionIds||$cont': regionId,
              'roleNames||$cont': ',Supervisor,',
              active: true,
            },
            sort: { field: 'firstName', order: 'ASC' },
            pagination: { page: 1, perPage: 1000 },
          },
        );
        setSupervisors(response.data);
      } catch (error) {
        console.error('Failed to load supervisors:', error);
        setSupervisors([]);
      } finally {
        setSupervisorsLoading(false);
      }
    },
    [dataProvider],
  );

  const loadBuilderSupervisors = useCallback(
    async (regionId: string): Promise<void> => {
      try {
        const response = await dataProvider.getList<UserRoleRegion>(
          'user-role-region-list',
          {
            filter: {
              'regionIds||$cont': regionId,
              'roleNames||$cont': ',BuilderSupervisor,',
              active: true,
            },
            sort: { field: 'firstName', order: 'ASC' },
            pagination: { page: 1, perPage: 1000 },
          },
        );
        setBuilderSupervisors(response.data);
      } catch (error) {
        console.error('Failed to load builder supervisors:', error);
        setBuilderSupervisors([]);
      }
    },
    [dataProvider],
  );

  const batchUpdateSupervisors = useCallback(
    async (
      subdivisionId: string,
      existingSupervisors: Array<{ id: string; userId: string }>,
      existingBuilderSupervisors: Array<{ id: string; userId: string }>,
      selectedSupervisorIds: string[],
      selectedBuilderSupervisorIds: string[],
    ): Promise<void> => {
      // Calculate supervisor changes
      const addedSupervisor: Array<{ userId: string; subdivisionId: string }> =
        [];
      const deletedSupervisor: string[] = [];

      existingSupervisors.forEach(item => {
        if (!selectedSupervisorIds.includes(item.userId)) {
          deletedSupervisor.push(item.id);
        }
      });

      selectedSupervisorIds.forEach(userId => {
        const exists = existingSupervisors.find(item => item.userId === userId);
        if (!exists) {
          addedSupervisor.push({ userId, subdivisionId });
        }
      });

      // Calculate builder supervisor changes
      const addedBuilderSupervisor: Array<{
        userId: string;
        subdivisionId: string;
      }> = [];
      const deletedBuilderSupervisor: string[] = [];

      existingBuilderSupervisors.forEach(item => {
        if (!selectedBuilderSupervisorIds.includes(item.userId)) {
          deletedBuilderSupervisor.push(item.id);
        }
      });

      selectedBuilderSupervisorIds.forEach(userId => {
        const exists = existingBuilderSupervisors.find(
          item => item.userId === userId,
        );
        if (!exists) {
          addedBuilderSupervisor.push({ userId, subdivisionId });
        }
      });

      // Execute batch operations
      await batchSupervisorSubdivision({
        insert: addedSupervisor,
        update: [],
        delete: deletedSupervisor,
      });

      await batchBuilderSupervisor({
        insert: addedBuilderSupervisor,
        update: [],
        delete: deletedBuilderSupervisor,
      });
    },
    [],
  );

  const loadSpSubdivisions = useCallback(
    async (subdivisionId: string): Promise<void> => {
      setSpSubdivisionsLoading(true);
      try {
        const response = await dataProvider.getList<SpSubdivisionNameInterface>(
          'sp-subdivision-name',
          {
            filter: { subdivisionId },
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'spName', order: 'ASC' },
          },
        );
        setSpSubdivisions(response.data);
      } catch (error) {
        console.error('Failed to load SP subdivisions:', error);
        setSpSubdivisions([]);
      } finally {
        setSpSubdivisionsLoading(false);
      }
    },
    [dataProvider],
  );

  const createSpSubdivision = useCallback(
    async (
      subdivisionId: string,
      spName: string,
      active: boolean,
      builderId: string,
    ): Promise<SpSubdivisionNameInterface | null> => {
      try {
        const result = await dataProvider.create<SpSubdivisionNameInterface>(
          'sp-subdivision-name',
          {
            data: {
              subdivisionId,
              spName,
              active,
              builderId,
            },
          },
        );
        setSpSubdivisions(prev => [...prev, result.data]);
        return result.data;
      } catch (error) {
        console.error('Failed to create SP subdivision:', error);
        return null;
      }
    },
    [dataProvider],
  );

  const updateSpSubdivisionActive = useCallback(
    async (
      id: string,
      active: boolean,
      previousData: SpSubdivisionNameInterface,
    ): Promise<void> => {
      try {
        await dataProvider.update('sp-subdivision-name', {
          id,
          data: { active },
          previousData,
        });
        setSpSubdivisions(prev =>
          prev.map(item => (item.id === id ? { ...item, active } : item)),
        );
      } catch (error) {
        console.error('Failed to update SP subdivision:', error);
        throw error;
      }
    },
    [dataProvider],
  );

  const clearSupervisors = useCallback(() => {
    setSupervisors([]);
    setBuilderSupervisors([]);
  }, []);

  return {
    // Primary subdivision state (SWR pattern via useGetOne + Redux)
    selectedSubdivision,
    isLoading, // true only on first load (no cache in Redux)
    isRevalidating, // true during any fetch
    subdivisionLoaded, // true once fetch completed (use to detect "not found")
    subdivisionError, // truthy on fetch failure (404, network, etc.)
    // Secondary state
    formLoading,
    supervisors,
    builderSupervisors,
    supervisorsLoading,
    selectedRegion,
    regionLoading,
    spSubdivisions,
    spSubdivisionsLoading,
    // Actions
    loadSubdivisionById,
    createSubdivision,
    updateSubdivision,
    loadRegion,
    loadSupervisors,
    loadBuilderSupervisors,
    batchUpdateSupervisors,
    loadSpSubdivisions,
    createSpSubdivision,
    updateSpSubdivisionActive,
    clearSupervisors,
  };
}

export const SubDivisionContainer = createContainer(useSubDivision);
